"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { usePublicAuth } from "@/lib/public-auth";
import { persistAttribution, extractAttributionFromLocation, getAttributionForEvent } from "@/utils/attribution";
import { phCapture, phGetDistinctId } from "@/utils/analytics";
import { captureCustomer } from "@/lib/actions/captureCustomer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DISCORD_URL } from "@/lib/landing-constants";
import { handleImageError } from "@/lib/cpa-offers";
import { sportsbookLogoUrl } from "@/lib/sportsbook-logo-url";
import { CPAOffersCarousel } from "@/components/cpa-offers-carousel";
import { CountdownCPAOffer } from "@/components/go/countdown-cpa-offer";
import Image from "next/image";
import { DiscordMark } from "@/components/ui/discord-mark";
import { isValidRedirectUrl } from "@/lib/redirect-links/validation";
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  TrendingUp,
  Lock,
} from "lucide-react";
import { INTERSTITIAL_TESTIMONIALS } from "@/lib/testimonial-data";

export function getPlatformName(url: string): string | null {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname.includes("prizepicks")) return "PrizePicks";
    if (hostname.includes("underdogfantasy")) return "Underdog";
    if (hostname.includes("sleeper")) return "Sleeper";
    if (hostname.includes("pick6")) return "Pick6";
    if (hostname.includes("parlayplay")) return "ParlayPlay";
    if (hostname.includes("dabble")) return "Dabble";
    if (hostname.includes("betrivers")) return "BetRivers";
    if (hostname.includes("betr")) return "Betr";
    if (hostname.includes("fliff")) return "Fliff";
    if (hostname.includes("draftkings")) return "DraftKings";
    if (hostname.includes("fanduel")) return "FanDuel";
    if (hostname.includes("betmgm")) return "BetMGM";
    if (hostname.includes("caesars")) return "Caesars";
    if (hostname.includes("pointsbet")) return "PointsBet";
    if (hostname.includes("fanatics")) return "Fanatics";
    if (hostname.includes("hardrock")) return "Hard Rock";
    if (hostname.includes("circa")) return "Circa";
    if (hostname.includes("pinnacle")) return "Pinnacle";
    if (hostname.includes("rebet")) return "Rebet";
    if (hostname.includes("kambi")) return hostname;
    if (hostname.includes("novig")) return "Novig";
    if (hostname.includes("prophetx")) return "ProphetX";
    if (hostname.includes("kalshi")) return "Kalshi";
    if (hostname.includes("polymarket")) return "Polymarket";
    if (hostname.includes("courtside")) return "Courtside";
    if (hostname.includes("boomfantasy") || hostname.includes("boomsports")) return "Boom";
    if (hostname.includes("chalkboard")) return "Chalkboard";
    if (hostname.includes("smacktok")) return "SmackTok";
    if (hostname.includes("4cx")) return "4Cx";
    if (hostname.includes("4casters")) return "4casters";
    if (hostname.includes("sx.bet")) return "SX Bet";
    if (hostname.includes("bet105.ag")) return "Bet105";
    if (hostname.includes("onyxodds.com")) return "Onyx Odds";
    return null;
  } catch {
    return null;
  }
}


const extendedSocialProof = [...INTERSTITIAL_TESTIMONIALS, ...INTERSTITIAL_TESTIMONIALS];

const sportsbookLogos = [
  { id: "PP", name: "PrizePicks", file: "PP.png" },
  { id: "UD", name: "Underdog", file: "UD.webp" },
  { id: "DK", name: "DraftKings", file: "DK.webp" },
  { id: "FD", name: "FanDuel", file: "FD.png" },
  { id: "P6", name: "Pick6", file: "P6.webp" },
  { id: "betr", name: "Betr", file: "betr.png" },
  { id: "FL", name: "Fliff", file: "FL.png" },
  { id: "MG", name: "MGM", file: "MG.webp" },
];

const extendedSportsbooks = [...sportsbookLogos, ...sportsbookLogos];

interface InterstitialClientProps {
  code: string;
  destinationUrl: string;
  influencerName: string;
  influencerHandle: string | null;
  referralCode: string;
  rewardfulVia?: string | null;
}

export function InterstitialClient({
  code,
  destinationUrl,
  influencerName,
  influencerHandle,
  referralCode,
  rewardfulVia,
}: InterstitialClientProps) {
  const { isAuthenticated } = usePublicAuth();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [hasPreviouslySubmitted, setHasPreviouslySubmitted] = useState(false);
  const [showSkipLink, setShowSkipLink] = useState(false);
  const hasFiredViewEvent = useRef(false);

  useEffect(() => {
    try {
      if (
        sessionStorage.getItem("email_captured") === "true" ||
        localStorage.getItem("email_captured") === "true"
      ) {
        setHasPreviouslySubmitted(true);
      }
    } catch {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowSkipLink(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const displayName = influencerHandle
    ? (influencerHandle.startsWith('@') ? influencerHandle : `@${influencerHandle}`)
    : influencerName;

  const platformName = getPlatformName(destinationUrl);
  const excludePlatformId = platformName?.toLowerCase() ?? null;

  const attribution = useMemo(
    () => ({
      via: referralCode,
      utm_source: "influencer",
      utm_medium: "social",
      utm_campaign: influencerName,
    }),
    [referralCode, influencerName]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      phCapture("influencer_discord_cta_viewed", {
        surface: "influencer_interstitial",
        code,
        influencer: influencerName,
        referral_code: referralCode,
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    persistAttribution(extractAttributionFromLocation());
    persistAttribution(attribution);

    if (rewardfulVia && typeof window !== "undefined") {
      const rewardfulQueue = (window as any).rewardful;
      if (typeof rewardfulQueue === "function") {
        rewardfulQueue("ready", () => {
          rewardfulQueue("source", rewardfulVia);
        });
      }
    }

  if (!hasFiredViewEvent.current) {
      hasFiredViewEvent.current = true;
      phCapture("influencer_link_viewed", {
        code,
        influencer: influencerName,
        influencer_handle: influencerHandle,
        referral_code: referralCode,
        destination: destinationUrl,
        rewardful_via: rewardfulVia || null,
      });
    }
  }, [
    attribution,
    code,
    influencerName,
    influencerHandle,
    referralCode,
    destinationUrl,
    rewardfulVia,
  ]);

  useEffect(() => {
    if (!submitted) return;

    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      if (isValidRedirectUrl(destinationUrl)) {
        window.location.href = destinationUrl;
      } else {
        window.location.href = "/";
      }
    }
  }, [submitted, countdown, destinationUrl]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const metaLeadEventId = crypto.randomUUID();
      const emailDomain = email.split("@")[1]?.toLowerCase() || null;

      phCapture("email_capture_submitted", {
        surface: "influencer_interstitial",
        email_domain: emailDomain,
        influencer: influencerName,
        referral_code: referralCode,
      });

      const phDistinctId = phGetDistinctId() || undefined;
      const mergedAttribution = getAttributionForEvent();
      const result = await captureCustomer(email, {
        ph_distinct_id: phDistinctId,
        attribution: mergedAttribution,
        meta_event_id: metaLeadEventId,
        event_source_url: typeof window !== "undefined" ? window.location.href : undefined,
        surface: "influencer_interstitial",
      });

      if (result.success) {
        setSubmitted(true);

        try {
          sessionStorage.setItem("email_captured", "true");
          localStorage.setItem("email_captured", "true");
        } catch {
        }

        phCapture("email_capture_succeeded", {
          surface: "influencer_interstitial",
          email_domain: emailDomain,
          outcome: result.outcome,
          influencer: influencerName,
          referral_code: referralCode,
        });
      } else {
        const errorMessage =
          result.intercom?.error ||
          result.stripe?.error ||
          "Failed to submit. Please try again.";
        setError(errorMessage);

        phCapture("email_capture_failed", {
          surface: "influencer_interstitial",
          email_domain: emailDomain,
          error_message: errorMessage,
          influencer: influencerName,
          referral_code: referralCode,
        });
      }
    } catch (err) {
      console.error("Error capturing customer:", err);
      setError("An unexpected error occurred. Please try again.");

      phCapture("email_capture_failed", {
        surface: "influencer_interstitial",
        error_message: err instanceof Error ? err.message : "Unknown error",
        influencer: influencerName,
        referral_code: referralCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipClick = useCallback(() => {
    if (rewardfulVia && typeof window !== "undefined") {
      const rewardfulQueue = (window as any).rewardful;
      if (typeof rewardfulQueue === "function") {
        rewardfulQueue("source", rewardfulVia);
      }
    }

    phCapture("influencer_continue_clicked", {
      code,
      influencer: influencerName,
      referral_code: referralCode,
    });

    if (isValidRedirectUrl(destinationUrl)) {
      window.location.href = destinationUrl;
    } else {
      window.location.href = "/";
    }
  }, [code, influencerName, referralCode, destinationUrl, rewardfulVia]);

  const fireCPAEvent = useCallback((event: string, offerId: string, surface: string, extra?: Record<string, string>) => {
    phCapture(event, {
      code,
      influencer: influencerName,
      referral_code: referralCode,
      offer_id: offerId,
      surface,
      ...extra,
    });
  }, [code, influencerName, referralCode]);

  const handleCarouselOfferClick = useCallback((offerId: string, link: string) => {
    const surface = submitted ? "carousel_post_submit" : "carousel_pre_submit";
    fireCPAEvent("influencer_cpa_offer_clicked", offerId, surface, { destination: link });
  }, [fireCPAEvent, submitted]);

  const handleCountdownOfferClick = useCallback((offerId: string, link: string) => {
    fireCPAEvent("influencer_cpa_offer_clicked", offerId, "countdown", { destination: link });
  }, [fireCPAEvent]);

  const handleCPAOfferShown = useCallback((offerId: string) => {
    fireCPAEvent("influencer_cpa_offer_shown", offerId, "countdown");
  }, [fireCPAEvent]);

  return (
    <div className="space-y-5">
      <div className="text-center space-y-2 animate-reveal-in" style={{ animationDelay: "100ms" }}>
        <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
          Unlock {displayName}&apos;s <span className="text-primary">slip</span>.
        </h1>
        <p className="text-muted-foreground text-sm">
          Enter your email to unlock this slip and get future picks from {displayName}.
        </p>
      </div>

      <>
        {!isAuthenticated && !submitted && !hasPreviouslySubmitted ? (
          <form
            key="email-form"
            onSubmit={handleEmailSubmit}
            className="space-y-3 animate-reveal-in"
            style={{ animationDelay: "200ms" }}
          >
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
              aria-label="Email address"
              className="h-12 text-base rounded-md border-border/60 bg-card transition-all shadow-sm"
            />
            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}
            <Button
              type="submit"
              disabled={isSubmitting || !email}
              className="group w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-semibold transition-all duration-200"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="size-5 animate-spin" />
                  Unlocking…
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  View Bet Slip
                  <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </Button>

            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground/70">
              <Lock className="size-3" />
              No spam, ever. Unsubscribe anytime.
            </p>

            <>
              {showSkipLink && (
                <div className="text-center pt-1 animate-reveal-in">
                  <button
                    type="button"
                    onClick={handleSkipClick}
                    className="text-xs text-muted-foreground/70 hover:text-muted-foreground underline decoration-muted-foreground/40 transition-colors"
                  >
                    Skip to bet slip
                  </button>
                </div>
              )}
            </>

          </form>
        ) : submitted ? (
          <div
            key="success-state"
            className="py-6 text-center space-y-4 animate-reveal-in"
          >
            <section className="space-y-3" role="status" aria-live="polite">
              <div className="inline-flex items-center gap-2 text-success font-medium">
                <CheckCircle2 className="size-5" />
                <span>Unlocked!</span>
              </div>
              <p className="text-muted-foreground">
                Redirecting to {displayName}&apos;s slip in{" "}
                <span className="data-num font-bold text-foreground tabular-nums">
                  {countdown}
                </span>
                ...
              </p>
            </section>

            <CountdownCPAOffer
              excludeOfferId={excludePlatformId}
              onOfferClick={handleCountdownOfferClick}
              onOfferShown={handleCPAOfferShown}
            />
          </div>
        ) : (isAuthenticated || hasPreviouslySubmitted) ? (
          <div
            key="auth-continue"
            className="space-y-3 animate-reveal-in"
            style={{ animationDelay: "200ms" }}
          >
            <Button
              onClick={handleSkipClick}
              className="group w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-semibold transition-all duration-200"
            >
              <span className="flex items-center justify-center gap-2">
                Continue to {displayName}&apos;s {platformName ? `${platformName} Slip` : "slip"}
                <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </Button>
          </div>
        ) : null}
      </>

      <div
        className="pt-2 relative overflow-hidden animate-reveal-in"
        style={{ animationDelay: "300ms" }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

        <div
          aria-hidden="true"
          className="flex py-1 w-max animate-marquee-left"
        >
          {extendedSocialProof.map((proof, i) => (
            <div
              key={`${proof.handle}-${i}`}
              className="flex-shrink-0 w-32 mx-1.5 relative rounded-md overflow-hidden border border-border/50 bg-card"
            >
              <div className="relative h-24 overflow-hidden">
                <Image
                  src={proof.imageSrc!}
                  alt={`${proof.name}'s results`}
                  fill
                  className="object-cover object-top"
                  sizes="128px"
                  onError={handleImageError}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
                <div className="absolute bottom-1 right-1">
                  <div className="flex items-center gap-0.5 bg-card/90 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] font-bold text-success">
                    <TrendingUp className="size-2.5" />
                    {proof.profit}
                  </div>
                </div>
              </div>
              <div className="p-1.5 flex items-center gap-1.5">
                <Image
                  src={proof.avatarSrc}
                  alt={proof.name}
                  width={16}
                  height={16}
                  className="rounded-full"
                  onError={handleImageError}
                />
                <p className="text-[9px] font-medium text-foreground truncate">
                  {proof.handle}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div
        className="pt-1 relative overflow-hidden animate-reveal-in"
        style={{ animationDelay: "350ms" }}
      >
        <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        <div
          aria-hidden="true"
          className="flex items-center py-1 w-max animate-marquee-right"
        >
          {extendedSportsbooks.map((book, index) => (
            <div
              key={`${book.id}-${index}`}
              className="flex-shrink-0 size-9 mx-2 rounded-md bg-card border border-border/30 flex items-center justify-center"
            >
              <Image
                src={sportsbookLogoUrl(book.file)}
                alt={book.name}
                width={22}
                height={22}
                className="object-contain opacity-60"
                onError={handleImageError}
              />
            </div>
          ))}
        </div>
      </div>

      <div
        className="pt-2 animate-reveal-in"
        style={{ animationDelay: "380ms" }}
      >
        <CPAOffersCarousel
          onOfferClick={handleCarouselOfferClick}
          excludeOfferId={excludePlatformId}
        />
      </div>

      <div
        className="pt-1 animate-reveal-in"
        style={{ animationDelay: "420ms" }}
      >
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Join the Upside Discord community, opens in new tab"
          onClick={() => phCapture("influencer_discord_cta_clicked", {
            surface: "influencer_interstitial",
            destination: DISCORD_URL,
            code,
            influencer: influencerName,
            referral_code: referralCode,
          }, { send_instantly: true })}
          className="group flex min-h-[44px] items-center justify-center gap-2.5 rounded-md border border-[#5865F2]/30 bg-[#5865F2]/10 px-4 py-3 transition-all hover:border-[#5865F2]/50 hover:bg-[#5865F2]/15"
        >
          <DiscordMark className="size-[18px]" aria-hidden="true" />
          <span className="text-xs font-medium text-[#7B8CFF]">
            Join our betting community
          </span>
          <ArrowRight className="size-3 text-[#7B8CFF]/60 transition-transform group-hover:translate-x-0.5" />
        </a>
      </div>

    </div>
  );
}
